import React from "react";
import { Helmet } from "react-helmet";
import { FormattedMessage, useIntl } from "react-intl";
import { useOutletContext } from "react-router";
import { PageWrapper } from "components/Containers";
import {
  RoleDescription,
  RoleDetails,
  RelatedRoles,
  RoleCodeChallenges,
  RoleQuestionnaire,
  RoleAttachments,
  RoleRequirements,
  RoleInterviewQuestions,
  RoleApplicationSteps,
} from "components/Missions/Roles";
import { MissionAboutClient } from "components/Missions";
import Card from "components/Card";
import { Grid } from "components/Containers";
import { useAuth } from "hooks";
import PageTitle from "components/PageTitle";

/**
 * Mission Role Details
 */
const MissionRoleDetails = () => {
  const { isAdmin, isClient, isTalentSimpleClient, canManageRoleAttachments, canViewUnpublishedRoles } = useAuth();
  const { selectedRole, mission, loading, refetch } = useOutletContext();
  const intl = useIntl();

  return (
    <>
      <Helmet>
        <title>{intl.formatMessage({ id: "Meta.Missions.Role.Title" })}</title>
      </Helmet>
      <PageWrapper>
        <Grid>
          <Grid.col start={1} end={13}>
            <PageTitle
              subtitle={
                <FormattedMessage
                  id={isTalentSimpleClient ? "Missions.MissionRole.Overview" : "Missions.Roles.Details.Title"}
                />
              }
            />
          </Grid.col>
          <Grid.col start={1} end={9}>
            <Grid>
              <Grid.col start={1} end={13}>
                <Card>
                  <RoleDescription
                    showCodeChallenges={false}
                    role={selectedRole}
                    isLoading={loading}
                    showArcheTypes={isAdmin}
                  />
                </Card>
              </Grid.col>
              {isAdmin && (
                <Grid.col start={1} end={13}>
                  <Card>
                    <RoleCodeChallenges data={selectedRole} loading={loading} />
                  </Card>
                </Grid.col>
              )}
              {isAdmin && (
                <Grid.col start={1} end={13}>
                  <Card>
                    <RoleQuestionnaire missionRole={selectedRole} loading={loading} refetch={refetch} />
                  </Card>
                </Grid.col>
              )}
              {isAdmin && (
                <Grid.col start={1} end={13}>
                  <Card>
                    <RoleRequirements missionRole={selectedRole} loading={loading} refetch={refetch} />
                  </Card>
                </Grid.col>
              )}
              {isAdmin && (
                <Grid.col start={1} end={13}>
                  <Card>
                    <RoleInterviewQuestions missionRole={selectedRole} />
                  </Card>
                </Grid.col>
              )}
              {canManageRoleAttachments && (
                <Grid.col start={1} end={13}>
                  <Card>
                    <RoleAttachments missionRole={selectedRole} loading={loading} refetch={refetch} />
                  </Card>
                </Grid.col>
              )}
            </Grid>
          </Grid.col>
          <Grid.col start={9} end={13}>
            <Grid>
              {(isAdmin || isClient) && (
                <Grid.col start={1} end={13}>
                  <Card>
                    <RoleApplicationSteps role={selectedRole} loading={loading} isReadOnly={isClient} />
                  </Card>
                </Grid.col>
              )}
              <Grid.col start={1} end={13}>
                <Card>
                  <RoleDetails role={selectedRole} mission={mission} loading={loading} />
                </Card>
              </Grid.col>
              <Grid.col start={1} end={13}>
                <Card>
                  <RelatedRoles
                    missionId={mission?.uuid}
                    isLoading={loading}
                    canViewUnpublishedRoles={canViewUnpublishedRoles}
                  />
                </Card>
              </Grid.col>
              {!mission?.isDefault && (
                <Grid.col start={1} end={13}>
                  <Card>
                    <MissionAboutClient mission={mission} isLoading={loading} />
                  </Card>
                </Grid.col>
              )}
            </Grid>
          </Grid.col>
        </Grid>
      </PageWrapper>
    </>
  );
};

export default MissionRoleDetails;
